/**
 * Per-domain reports library index page (M3X.UIPOLISH PR 10-14).
 *
 * Single page powers /reports/library/loans, /reports/library/leaves,
 * /reports/library/eos, /reports/library/attendance, /reports/library/
 * payroll, /reports/library/compliance, /reports/library/ai_insights.
 *
 * Consumes the locked REPORTS_REGISTRY. Each report renders as a
 * SettingsEntryCard with bilingual title + description + cadence +
 * persona + cut metadata in the meta line.
 *
 * Per spec §1.B — this is the discoverable index per domain.
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import {
  EmptyState,
  PageHeader,
  SettingsEntryCard,
  SettingsSection,
} from "../../components/ui";
import {
  REPORT_COUNTS,
  REPORTS_REGISTRY,
  reportsByDomain,
} from "../../data/reports_registry";
import type { ReportDomain } from "../../components/ui/ReportPage";

const DOMAIN_TITLE_KEYS: Record<ReportDomain, string> = {
  loans: "reports_lib.domain_loans_title",
  leaves: "reports_lib.domain_leaves_title",
  eos: "reports_lib.domain_eos_title",
  attendance: "reports_lib.domain_attendance_title",
  payroll: "reports_lib.domain_payroll_title",
  compliance: "reports_lib.domain_compliance_title",
  headcount: "reports_lib.domain_headcount_title",
  ai_insights: "reports_lib.domain_ai_insights_title",
};

const DOMAIN_DESCRIPTION_KEYS: Record<ReportDomain, string> = {
  loans: "reports_lib.domain_loans_desc",
  leaves: "reports_lib.domain_leaves_desc",
  eos: "reports_lib.domain_eos_desc",
  attendance: "reports_lib.domain_attendance_desc",
  payroll: "reports_lib.domain_payroll_desc",
  compliance: "reports_lib.domain_compliance_desc",
  headcount: "reports_lib.domain_headcount_desc",
  ai_insights: "reports_lib.domain_ai_insights_desc",
};


export function LibraryByDomainPage() {
  const { t } = useTranslation();
  const { domain } = useParams<{ domain: ReportDomain }>();
  const [searchTerm, setSearchTerm] = useState("");

  const isValidDomain = Boolean(
    domain && Object.keys(DOMAIN_TITLE_KEYS).includes(domain),
  );
  const reports = useMemo(
    () => (isValidDomain ? reportsByDomain(domain as ReportDomain) : []),
    [domain, isValidDomain],
  );
  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return reports;
    const needle = searchTerm.toLowerCase();
    return reports.filter(
      (r) =>
        r.id.toLowerCase().includes(needle) ||
        t(r.titleKey, r.id).toLowerCase().includes(needle) ||
        t(r.descriptionKey, "").toLowerCase().includes(needle),
    );
  }, [reports, searchTerm, t]);

  if (!isValidDomain) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={t("reports_lib.not_found", "Domain not found")} />
        <EmptyState
          headline={t(
            "reports_lib.not_found_headline",
            "No reports for this domain",
          )}
          description={t(
            "reports_lib.not_found_desc",
            "The 8 supported domains are: loans, leaves, eos, attendance, payroll, compliance, headcount, ai_insights.",
          )}
        />
      </div>
    );
  }

  const dom = domain as ReportDomain;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t(DOMAIN_TITLE_KEYS[dom], dom)}
        subtitle={t(DOMAIN_DESCRIPTION_KEYS[dom], "")}
        breadcrumbs={[
          {
            label: t("reports_lib.title", "Reports"),
            href: "/reports",
          },
          { label: t(DOMAIN_TITLE_KEYS[dom], dom) },
        ]}
      />

      <div className="relative max-w-md">
        <input
          type="search"
          placeholder={t(
            "reports_lib.search_placeholder",
            "Search this domain…",
          )}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full rounded-lg border border-border-subtle bg-surface-default px-3 py-2 text-sm placeholder:text-text-tertiary focus:border-ai focus:outline-none focus:ring-2 focus:ring-ai/20"
          aria-label={t(
            "reports_lib.search_placeholder",
            "Search this domain",
          )}
        />
      </div>

      {filtered.length === 0 ? (
        searchTerm.trim() ? (
          <EmptyState
            headline={t(
              "reports_lib.no_match",
              "No reports match your search",
            )}
          />
        ) : (
          <EmptyState
            headline={t(
              "reports_lib.empty_headline",
              "No reports in this domain yet",
            )}
            description={t(
              "reports_lib.empty_desc",
              "Reports appear here as their data sources come online.",
            )}
          />
        )
      ) : (
        <SettingsSection
          title={t(
            "reports_lib.count_label",
            "{{count}} reports",
            { count: reports.length } as Record<string, number>,
          )}
        >
          {filtered.map((r) => (
            <SettingsEntryCard
              key={r.id}
              icon={r.icon}
              title={t(r.titleKey, r.id)}
              description={t(r.descriptionKey, "")}
              to={r.to}
              meta={`${t(`reports_lib.cadence_${r.cadence}`, r.cadence)} · ${t(`reports_lib.persona_${r.persona}`, r.persona)}`}
              tone="indigo"
              comingSoon={r.comingSoon}
            />
          ))}
        </SettingsSection>
      )}

      <p className="text-xs text-text-tertiary">
        {t(
          "reports_lib.total_summary",
          "Catalog total: {{total}} reports across {{loans}} loans, {{leaves}} leaves, {{eos}} EOS, {{attendance}} attendance, {{payroll}} payroll, {{compliance}} compliance, {{ai}} AI insights.",
          {
            total: REPORTS_REGISTRY.length,
            loans: REPORT_COUNTS.loans,
            leaves: REPORT_COUNTS.leaves,
            eos: REPORT_COUNTS.eos,
            attendance: REPORT_COUNTS.attendance,
            payroll: REPORT_COUNTS.payroll,
            compliance: REPORT_COUNTS.compliance,
            ai: REPORT_COUNTS.ai_insights,
          } as Record<string, number>,
        )}
      </p>
    </div>
  );
}

export default LibraryByDomainPage;
